import ApiGet from "../../../../../api-module/api-configuration/get-api/getApi";
import { API_ENDPOINTS } from "../../apiGroup/apiProvider";
import type { CmsFindBySlugResponse } from "./appContent.types";

// appContentShellCache.ts
type CacheEntry<T> = { data: T; expiresAt: number };

const TTL_MS = 10 * 60 * 1000;
const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

const keyOf = (slug: string) => `cms:page:${slug}`;

export function getCachedCmsPage<T = unknown>(slug: string): T | null {
  const cached = cache.get(keyOf(slug));
  if (!cached) return null;
  if (Date.now() >= cached.expiresAt) return null;
  return cached.data as T;
}

export function prefetchCmsPage(slug: string) {
  // fire-and-forget; inflight map prevents duplicates
  void fetchCmsPage(slug);
}

// ...keep your existing fetchCmsPage unchanged...
export async function fetchCmsPage<T = unknown>(
  slug: string,
  signal?: AbortSignal
): Promise<T> {
  const key = keyOf(slug);

  const cached = cache.get(key);
  if (cached && Date.now() < cached.expiresAt) return cached.data as T;

  const running = inflight.get(key);
  if (running) return running as Promise<T>;

  const req = ApiGet<CmsFindBySlugResponse<T>>(
    API_ENDPOINTS.FIND_BY_SLUG,
    { params: { slug }, signal }
  )
    .then((res) => {
      const pageData = res.data.data.data;
      cache.set(key, { data: pageData, expiresAt: Date.now() + TTL_MS });
      return pageData;
    })
    .finally(() => inflight.delete(key));

  inflight.set(key, req);
  return req;
}
